import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { sendReport, wasReported } from './ReportFlag';

// ⋯ on a ghost line (Nina 09-29: the 🚩 was "too front and center on the ghost
// chats. Maybe it should be under a '...' menu?"). A quiet ⋯ opens a small menu:
// "🚩 Report", then the same ask-first step as ReportFlag (never a bare one-tap:
// accidental reports would poison the 3-distinct-reporters threshold).
//
// The ⋯ rides the line's hover / tap reveal (utils/ghostLook); the page keeps
// the line revealed while the menu is open (onOpenChange). Mechanics are
// PortedMenu's: portaled to <body> (the ghost panel scrolls and clips), fixed,
// following the ⋯ on scroll/resize; an outside press or Esc closes. The CSS is
// utils/ghostLook's one stylesheet: class names avoid the substrings generated
// themes target, and the buttons carry the theme shield (data-prop-ui).
//
// Sweep #161 review fixes: (1) the portaled menu sits one layer above whatever
// holds its ⋯ (the phone ghost sheet is a fixed overlay at z-index 150000: at a
// flat 100020 the menu opened hidden under it); (2) the virtual feed keys rows by
// index, so a shifting feed can hand this ⋯ to another line mid-menu: the menu
// closes, and a report always files the line the menu opened on.
const MENU_Z = 100020;
export const layerAbove = (el) => {
  let z = MENU_Z;
  try {
    for (let n = el; n && n.nodeType === 1 && n !== document.body; n = n.parentElement) {
      const v = parseInt(window.getComputedStyle(n).zIndex, 10);
      if (Number.isFinite(v) && v >= z) z = v + 1;
    }
  } catch (e) { /* the floor stands */ }
  return z;
};

export default function GhostLineMenu({ targetId, targetPlayerId = null, kind = 'chat_message', onOpenChange = null }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [z, setZ] = useState(MENU_Z);
  const [step, setStep] = useState('menu'); // menu · confirm · sending · done
  const [closeSoon, setCloseSoon] = useState(false);
  const btnRef = useRef(null);
  const sheetRef = useRef(null);
  const cancelRef = useRef(null);
  const openedOn = useRef(null); // the line the menu opened on
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;

  const show = useCallback((v) => {
    setOpen(v);
    if (v) {
      openedOn.current = { targetId, targetPlayerId };
      setStep(wasReported(targetId) ? 'done' : 'menu');
      setZ(layerAbove(btnRef.current));
    } else {
      setPos(null);
    }
    if (onOpenChangeRef.current) onOpenChangeRef.current(v);
  }, [targetId, targetPlayerId]);

  useEffect(() => {
    if (open && openedOn.current && openedOn.current.targetId !== targetId) show(false);
  }, [open, targetId, show]);

  const place = useCallback(() => {
    const b = btnRef.current;
    if (!b || typeof window === 'undefined') return;
    const r = b.getBoundingClientRect();
    const vw = window.innerWidth || 0;
    const vh = window.innerHeight || 0;
    const left = Math.max(8, Math.min(vw - 8 - 180, r.right - 180));
    // below the ⋯, or above it near the bottom of the screen (the ask grows it)
    setPos(r.bottom + 150 < vh ? { top: r.bottom + 4, left } : { bottom: vh - r.top + 4, left });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    place();
    let raf = 0;
    const follow = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(place); };
    const onDown = (e) => {
      if (btnRef.current && btnRef.current.contains(e.target)) return;
      if (sheetRef.current && sheetRef.current.contains(e.target)) return;
      show(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      show(false);
      if (btnRef.current) btnRef.current.focus({ preventScroll: true });
    };
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, place, show]);

  // Keyboard users land inside once per open; the ask focuses Cancel, the safe answer.
  const placed = !!pos;
  useEffect(() => {
    if (!open || !placed || !sheetRef.current) return;
    const first = sheetRef.current.querySelector('button.gxm-item');
    if (first) first.focus({ preventScroll: true });
  }, [open, placed]);
  useEffect(() => {
    if (open && step === 'confirm' && cancelRef.current) cancelRef.current.focus({ preventScroll: true });
  }, [open, step]);

  useEffect(() => {
    if (!closeSoon) return undefined;
    const t = setTimeout(() => { setCloseSoon(false); show(false); }, 1200);
    return () => clearTimeout(t);
  }, [closeSoon, show]);

  if (!targetId) return null;

  const report = async () => {
    setStep('sending');
    try {
      await sendReport({ kind, ...(openedOn.current || { targetId, targetPlayerId }) });
      setStep('done');
      setCloseSoon(true);
    } catch (err) {
      console.error('[GhostLineMenu] Report failed:', err.message);
      setStep('menu');
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-prop-ui=""
        className="gxm-dots"
        aria-label="More"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => show(!open)}
      >
        ⋯
      </button>
      {open && pos && typeof document !== 'undefined' ? createPortal(
        <div ref={sheetRef} className="gxm-sheet" role="menu" aria-label="Line actions" style={{ position: 'fixed', zIndex: z, width: 180, ...pos }}>
          {step === 'menu' && (
            <button type="button" role="menuitem" data-prop-ui="" className="gxm-item" onClick={() => setStep('confirm')}>
              <span aria-hidden="true">🚩</span>Report
            </button>
          )}
          {(step === 'confirm' || step === 'sending') && (
            <>
              <div className="gxm-cap">Report this line?</div>
              <button type="button" role="menuitem" data-prop-ui="" className="gxm-item gxm-red" disabled={step === 'sending'} onClick={report}>
                {step === 'sending' ? '…' : 'Yes, report'}
              </button>
              <button ref={cancelRef} type="button" role="menuitem" data-prop-ui="" className="gxm-item" onClick={() => setStep('menu')}>
                Cancel
              </button>
            </>
          )}
          {step === 'done' && <div role="menuitem" aria-disabled="true" className="gxm-item gxm-quiet">✓ Reported</div>}
        </div>,
        document.body,
      ) : null}
    </>
  );
}
